/**
 * apiFetchAuthWithRefresh — the single source of truth for authenticated,
 * tenant-scoped, silent-refresh fetch against the Tyndo gateway (A5-441).
 *
 * Before this module, `useApi.ts` (`createFetchWithAuth`) and `useApiClient.ts`
 * (`fetchWithSilentRefresh` + the inline `request`/`blobRequest` bodies) each
 * carried a near-identical copy of:
 *   - header normalization (Headers | string[][] | Record → flat record),
 *   - JSON content-type defaulting,
 *   - `X-Tenant-Id` injection,
 *   - per-request timeout composition with the caller's abort signal,
 *   - one-shot 401 → silent token refresh → retry (skipping `dev-token-*`),
 *   - 401/403 `auth:*` CustomEvent dispatch.
 *
 * Any change to auth/timeout/refresh therefore had to be made twice and could
 * silently diverge. This module owns that logic once; `useApi.ts` and
 * `useApiClient.ts` both build their fetch via {@link createAuthenticatedFetch}
 * and keep only their own *response decoding* (ApiError objects vs Error
 * instances, JSON vs blob), which is genuinely call-site specific.
 */

import { resolveApiBaseUrl } from './apiFetch.ts';
import { refreshAccessToken } from './tokenRefresh.ts';
import { buildTimeoutSignal } from './abortSignal.ts';

/** Default client timeout for JSON requests. */
export const DEFAULT_TIMEOUT_MS = 30_000;
/** Larger default for blob downloads and FormData uploads. */
export const DEFAULT_TRANSFER_TIMEOUT_MS = 120_000;

const normalize = (url: string) => url.replace(/^\/api\/v\d+/, '');

/** Build the full gateway URL, preserving an already-supplied `/api/vN` prefix. */
export const buildFullUrl = (endpoint: string): string =>
  endpoint.startsWith('http') ? endpoint : `${resolveApiBaseUrl()}${normalize(endpoint)}`;

/**
 * Normalize a `HeadersInit` (Headers | string[][] | Record) into a flat,
 * mutable record we can safely augment with auth/tenant headers.
 */
export const normalizeHeaders = (headersInit?: HeadersInit): Record<string, string> => {
  const headers: Record<string, string> = {};
  if (!headersInit) return headers;
  if (headersInit instanceof Headers) {
    headersInit.forEach((value, key) => {
      headers[key] = value;
    });
  } else if (Array.isArray(headersInit)) {
    for (const [key, value] of headersInit as [string, string][]) headers[key] = value;
  } else {
    Object.assign(headers, headersInit as Record<string, string>);
  }
  return headers;
};

/** Options accepted by the authenticated fetch. */
export interface AuthenticatedFetchInit extends Omit<RequestInit, 'body'> {
  /** Plain JSON body — auto-stringified, `Content-Type: application/json`. */
  json?: unknown;
  /** Raw body — passed through untouched (FormData / Blob / string). */
  body?: BodyInit | null;
  /** Per-request client timeout in ms. Composed with any caller `signal`. */
  timeoutMs?: number;
}

/**
 * A request to the gateway, already authenticated (Bearer + tenant headers),
 * with a one-shot silent token refresh + retry applied on a 401. Returns the
 * raw `Response` so each caller can decode it (JSON vs blob) and apply its own
 * error semantics. `auth:unauthorized` / `auth:forbidden` events are dispatched
 * here so every call site behaves identically on auth failure.
 */
export type AuthenticatedFetch = (
  endpoint: string,
  init?: AuthenticatedFetchInit,
) => Promise<Response>;

/**
 * Dispatch the canonical auth-failure DOM event for a 401/403 response. The
 * dev-token bypass (`dev-token-*`) suppresses the 401 logout so a developer
 * session before Keycloak is configured doesn't get bounced.
 */
export const dispatchAuthEvent = (status: number, token: string | null): void => {
  if (typeof window === 'undefined') return;
  if (status === 401) {
    if (!token?.startsWith('dev-token-')) {
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
  } else if (status === 403) {
    window.dispatchEvent(new CustomEvent('auth:forbidden'));
  }
};

/**
 * Create an authenticated fetch bound to a token + tenant. The returned
 * function:
 *   1. builds the full URL,
 *   2. normalizes headers and injects `Authorization` / `X-Tenant-Id`,
 *   3. JSON-stringifies `json` (when present) and sets the content-type,
 *   4. composes the per-request timeout with the caller's abort signal,
 *   5. issues the request and, on a 401 for a non-dev token, performs ONE
 *      silent `refreshAccessToken()` + retry before returning,
 *   6. dispatches `auth:unauthorized` / `auth:forbidden` on a final 401/403.
 *
 * It deliberately does NOT parse the body or throw on non-2xx — the caller owns
 * that, because `useApi` throws plain `ApiError` objects while `useApiClient`
 * throws `Error` instances (and also serves blobs). This keeps a single network
 * + auth path while preserving both public surfaces.
 *
 * FormData / Blob bodies are passed by reference, so the silent-refresh retry
 * re-sends the same body safely (no stream is consumed before the retry).
 */
export const createAuthenticatedFetch = (
  token: string | null,
  tenantId: string | null,
): AuthenticatedFetch => {
  return async (endpoint, init = {}) => {
    const fullUrl = buildFullUrl(endpoint);
    const headers = normalizeHeaders(init.headers);

    let body: BodyInit | null | undefined = init.body ?? null;
    if (init.json !== undefined) {
      headers['Content-Type'] = headers['Content-Type'] ?? 'application/json';
      body = JSON.stringify(init.json);
    }

    if (tenantId && !headers['X-Tenant-Id']) {
      headers['X-Tenant-Id'] = tenantId;
    }

    const { timeoutMs, signal, json: _json, headers: _headers, ...restInit } = init;
    const method = init.method ?? (init.json !== undefined ? 'POST' : 'GET');

    const doFetch = (authToken: string | null): Promise<Response> =>
      fetch(fullUrl, {
        ...restInit,
        method,
        headers: authToken ? { ...headers, Authorization: `Bearer ${authToken}` } : { ...headers },
        body,
        signal: buildTimeoutSignal(timeoutMs ?? DEFAULT_TIMEOUT_MS, signal),
      });

    let response = await doFetch(token);

    // One silent refresh + retry before surfacing 401. A short-lived access
    // token expiring mid-session is renewed instead of bouncing the user.
    // Concurrent 401s share one refresh (single-flight in tokenRefresh).
    if (response.status === 401 && !token?.startsWith('dev-token-')) {
      const newToken = await refreshAccessToken();
      if (newToken && newToken !== token) {
        response = await doFetch(newToken);
      }
    }

    if (response.status === 401 || response.status === 403) {
      dispatchAuthEvent(response.status, token);
    }

    return response;
  };
};
